import { abnNoticeConstants, abnOptionMapping } from "constants/abnNotice";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { useContext, useState } from "react";
import { useCreateAbnNoticeMutation } from "store/queries/abnNotices";
import {
  ICreateAbnNoticeRequest,
  AbnTemplateType,
  AbnOption,
  IDocumentHashDataDto,
} from "types/AbnNotice/AbnNotice";
import { IPatient } from "types/Patient";

import { StyledSectionContainer, StyledHeaderContainer } from "../../styled";
import { OptionSelector } from "./OptionSelector";
import ProceduresList from "./ProceduresList";
import { SignaturePad } from "./SignaturePad";

interface IAbnSelection {
  procedures: {
    cptCode: string;
    cptDescription: string;
    amount: number;
  }[];
  patient: IPatient;
  selectedAbnOption: string;
  setSelectedAbnOption: (option: string) => void;
  orderId: string;
  refetchOrder: () => void;
}

const options = [
  {
    label:
      "I want the service(s) listed above. Bill Medicare for an official decision. I understand I am responsible for payment if Medicare doesn't pay, but I can appeal.",
    value: "billMedicare",
  },
  {
    label:
      "I want the service(s) listed above, but do not bill Medicare. I am responsible for payment and cannot appeal.",
    value: "selfPay",
  },
  {
    label:
      "I do not want the service(s) listed above. I am responsible for payment and cannot appeal.",
    value: "declineService",
  },
];

export default function AbnSelection({
  procedures,
  patient,
  selectedAbnOption,
  setSelectedAbnOption,
  orderId,
  refetchOrder,
}: IAbnSelection) {
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [createAbnNoticeMutation, { isLoading: isCreatingAbnNotice }] =
    useCreateAbnNoticeMutation();
  const { current: toastElement } = useContext(ToastContext);

  const handleSignatureChange = (signature: string | null) => {
    setSignatureData(signature);
  };

  const handleSubmit = () => {
    if (!signatureData || !selectedAbnOption) return;
    const abnOption = abnOptionMapping[selectedAbnOption] as AbnOption;
    if (!abnOption) return;
    const templateType = abnNoticeConstants.templateType
      .ABN_Medicare as AbnTemplateType;
    const documentHashData = {
      availableOptions: options,
      selectedOption: options.find(
        (option) => option.value === selectedAbnOption
      ),
    } as IDocumentHashDataDto;
    const createAbnNoticeRequest: ICreateAbnNoticeRequest = {
      orderId,
      signatureImageBase64: signatureData,
      templateType,
      abnOption,
      procedures,
      documentHashData,
    };

    createAbnNoticeMutation(createAbnNoticeRequest)
      .unwrap()
      .then((response) => {
        toastElement?.show({
          severity: "success",
          summary: "Success",
          detail: "ABN notice created successfully",
        });
        refetchOrder();
        console.log(response, "response");
      })
      .catch((error) => {
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: error?.data?.message || "Failed to create ABN notice",
        });
      });
  };

  const isSubmitEnabled =
    selectedAbnOption && selectedAbnOption !== "" && !!signatureData;

  console.log(procedures, "procedures");
  return (
    <div className="px-3 flex flex-column">
      <StyledSectionContainer className="pt-0">
        <StyledHeaderContainer>
          <p className="text-sm font-bold gray-text">
            Advance Beneficiary Notice of Non-coverage (ABN)
          </p>
          <p className="text-xs font-normal light-gray">
            Patient: {patient.firstName} {patient.lastName}
          </p>
        </StyledHeaderContainer>
        <div className="flex flex-column gap-2 px-3">
          <ProceduresList procedures={procedures} />
          <OptionSelector
            selectedAbnOption={selectedAbnOption}
            setSelectedAbnOption={setSelectedAbnOption}
            options={options}
          />
          <SignaturePad onSignatureChange={handleSignatureChange} />
          <div className="flex flex-column gap-2 mt-3 pb-3">
            <Button
              type="button"
              label="Select an option and sign"
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
              loading={isCreatingAbnNotice}
              className="p-button-primary w-full p-button-sm"
            />
          </div>
        </div>
      </StyledSectionContainer>
    </div>
  );
}
